import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAppDispatch, useAppSelector } from '../store/store';
import {
  environmentConfigSelector,
  getFeatureFlagByNameSelector,
  storeIdentifierNameSelector,
} from '../store/selectors/commonSelector';
import {
  searchInputSelector,
  searchDebouncedInputSelector,
  searchDataSelector,
  searchProductInputSelector,
  searchFireSaytImpressionSelector,
  defaultSearchTermSelector,
} from '../store/selectors/saytSelector';
import {
  setDebouncedSearchInput,
  setSearchData,
  setFireSaytImpression,
  setSearchDataLoading,
} from '../store/slices/sayt';
import { SearchSuggestionResult, SearchProductResult } from '../types';
import { getSayTEndpoint } from '../utility/utility';
import { envData } from '../environments';
import useLogger from './useLogger';

declare global {
  interface Window {
    akamaiSwimlaneCookie?: string;
  }
}

const useSAYT = () => {
  const dispatch = useAppDispatch();
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const searchInput = useAppSelector(searchInputSelector);
  const debouncedSearchInput = useAppSelector(searchDebouncedInputSelector);
  const searchData = useAppSelector(searchDataSelector);
  const searchProductsInput = useAppSelector(searchProductInputSelector);
  const defaultSearchTerm = useAppSelector(defaultSearchTermSelector);
  const fireSaytImpression = useAppSelector(searchFireSaytImpressionSelector);
  const shorterDelaySAYTEnabled = useAppSelector(
    getFeatureFlagByNameSelector('shorterDelaySAYTEnabled'),
  );
  const saytRedesignEnabled = useAppSelector(getFeatureFlagByNameSelector('saytRedesignEnabled'));
  const environmentConfig = useAppSelector(environmentConfigSelector);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { warn } = useLogger();

  const getDebounceDelay = () => {
    if (saytRedesignEnabled) {
      return 50;
    }

    if (shorterDelaySAYTEnabled) {
      return 250;
    }

    return 500;
  };

  const debounceDelay = getDebounceDelay();

  const getSayTApi = useCallback(() => {
    return environmentConfig?.api.sayt[storeIdentifierName]
      ? getSayTEndpoint(environmentConfig.api.sayt[storeIdentifierName].baseUrl)
      : getSayTEndpoint(environmentConfig.api.sayt.dsg.baseUrl);
  }, [environmentConfig, storeIdentifierName]);

  const store = envData[storeIdentifierName as keyof typeof envData]?.storeId;

  const searchSuggestionsApi = `${getSayTApi()}v1/suggest/${store}?keyword=`;
  const searchProductsApi = `${getSayTApi()}v1/products/${store}`;

  const saytUsedEventEmitter = () => {
    document.dispatchEvent(new CustomEvent('SAYTImpression'));
    dispatch(setFireSaytImpression(false));
  };

  const hasSaytResponseData = (saytData: SearchSuggestionResult): boolean => {
    return (
      saytData.keywords?.length > 0 ||
      saytData.brands?.length > 0 ||
      saytData.categories?.length > 0 ||
      saytData.products?.length > 0
    );
  };

  const callSAYTAPI = useCallback(
    async (type: 'suggestions' | 'products') => {
      const controller = new AbortController();
      const { signal } = controller;

      const url =
        type === 'suggestions'
          ? `${searchSuggestionsApi}${searchInput}&products=true`
          : `${searchProductsApi}?${searchProductsInput.type}=${searchProductsInput.term}`;

      try {
        const headers =
          typeof window !== 'undefined' && window.akamaiSwimlaneCookie
            ? {
                [`x-${storeIdentifierName}-swimlane`]: window.akamaiSwimlaneCookie,
              }
            : {};

        const response = await axios.get(url, {
          signal,
          timeout: 8000,
          headers,
        });

        if (response.status === 200 && response.data) {
          if (
            type === 'suggestions' &&
            searchInput.length > 2 &&
            fireSaytImpression &&
            hasSaytResponseData(response.data)
          ) {
            saytUsedEventEmitter();
          }

          const newData =
            type === 'suggestions'
              ? {
                  suggestions: response.data as SearchSuggestionResult,
                  products: response.data.products as SearchProductResult[],
                }
              : {
                  ...searchData,
                  products: response.data as SearchProductResult[],
                };

          dispatch(setSearchData(newData));
        }
      } catch (e) {
        warn(`DSG Search Failed: ${type}`, e as string);
      } finally {
        dispatch(setSearchDataLoading(false));
      }

      return () => controller.abort();
    },
    [
      dispatch,
      searchSuggestionsApi,
      searchProductsApi,
      searchInput,
      searchProductsInput,
      searchData,
      fireSaytImpression,
      storeIdentifierName,
    ],
  );

  // Debounced search input
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      dispatch(setDebouncedSearchInput(searchInput));
    }, debounceDelay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [searchInput, debounceDelay, dispatch]);

  // Trigger SAYT Suggestions
  useEffect(() => {
    if (searchInput && searchInput.length > 2 && !defaultSearchTerm) {
      callSAYTAPI('suggestions');
    }
  }, [debouncedSearchInput]);

  // Trigger SAYT Products
  useEffect(() => {
    if (searchProductsInput?.term) {
      callSAYTAPI('products');
    }
  }, [searchProductsInput]);

  // On initial load - check for query param
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // adding the below check to handle the on load API call for dsg, pl and gg. F
    // For g3, the onload call will not run as G3 search component doesnt handle this as of now.
    // Will remove this once G3 search component is refactored to handle this.
    const params = new URLSearchParams(window.location.search);
    const searchTerm = params.get('searchTerm');

    if (searchTerm) {
      callSAYTAPI('suggestions');
    }
  }, [storeIdentifierName]);

  return null;
};

export default useSAYT;
